import { isNil } from 'lodash';
import { useMemo, useRef } from 'react';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { hasRelatedCardDefaultLookup } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import {
  PREFILL_RELATED_CARD_FROM_ANOTHER_RELATED_CARD,
  useFeatureFlag,
} from '@shared/util/featureFlags';
import { PrefillWrites, useRelatedCardPrefill } from './useRelatedCardPrefill';

interface RelatedCardPrefillsProps {
  workflowTemplateId?: string | null;
  fields: FieldRecord;
  updateField: UpdateField;
  entityId?: string;
}

/**
 * Renderless. Mounted at the form level so related card fields configured to prefill from
 * another field get their value even when they are not visible on the form (e.g. hidden by
 * the view template).
 *
 * This is a create-form-only behavior, like any other default: an existing card's saved data is
 * never touched, so this doesn't render at all when editing an existing card (`entityId` is set).
 */
export function RelatedCardPrefills({
  workflowTemplateId,
  fields,
  updateField,
  entityId,
}: RelatedCardPrefillsProps) {
  const isEnabled = useFeatureFlag(PREFILL_RELATED_CARD_FROM_ANOTHER_RELATED_CARD);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId ?? undefined);
  // One record for the whole form, so a prefill can tell a value another prefill wrote from one
  // the person using the form picked. See `PrefillWrites`.
  const prefillWrites = useRef<PrefillWrites>(new Map()).current;

  const prefilledComponents = useMemo(
    () => (workflowTemplate?.components ?? []).filter(hasRelatedCardDefaultLookup),
    [workflowTemplate?.components],
  );

  if (!isEnabled || !isNil(entityId)) {
    return null;
  }

  return (
    <>
      {prefilledComponents.map(component => (
        <RelatedCardPrefill
          key={component.id}
          component={component}
          fields={fields}
          updateField={updateField}
          workflowTemplateId={workflowTemplateId}
          prefillWrites={prefillWrites}
        />
      ))}
    </>
  );
}

interface RelatedCardPrefillProps {
  component: API.RelatedCardComponent;
  fields: FieldRecord;
  updateField: UpdateField;
  workflowTemplateId?: string | null;
  prefillWrites: PrefillWrites;
}

function RelatedCardPrefill({
  component,
  fields,
  updateField,
  workflowTemplateId,
  prefillWrites,
}: RelatedCardPrefillProps) {
  useRelatedCardPrefill({ component, fields, updateField, workflowTemplateId, prefillWrites });
  return null;
}
